import React from 'react';
import {
  View,
  TouchableWithoutFeedback,
  StyleSheet,
  Platform,
  StyleProp,
  ViewStyle,
  GestureResponderEvent,
} from 'react-native';
import {Link, Route, useTheme} from '@react-navigation/native';
import Color from 'color';

import TabBarIcon from './TabBarIcon';
import type {BottomTabBarButtonProps} from './types';

type Props = {
  focused: boolean;
  route: Route<string>;
  icon?: (props: {focused: boolean; size: number; color: string}) => React.ReactNode;
  badge?: number | string;
  to?: string;
  uscIsBlocked?: boolean;
  button?: (props: BottomTabBarButtonProps) => React.ReactNode;
  onPress: (e: React.MouseEvent<HTMLElement, MouseEvent> | GestureResponderEvent) => void;
  onLongPress: (e: GestureResponderEvent) => void;
  horizontal: boolean;
  style?: StyleProp<ViewStyle>;
};

const defaultButtonRender = ({children, style, onPress, to, accessibilityRole, ...rest}: BottomTabBarButtonProps) => {
  if (Platform.OS === 'web' && to) {
    // React Native Web doesn't forward `onClick` if we use `TouchableWithoutFeedback`.
    // We need to use `onClick` to be able to prevent default browser handling of links.
    return (
      <Link
        {...(rest as any)}
        to={to as any}
        style={[styles.button, style] as any}
        onPress={(e: any) => {
          if (
            !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) && // ignore clicks with modifier keys
            (e.button == null || e.button === 0) // ignore everything but left clicks
          ) {
            e.preventDefault();
            onPress?.(e);
          }
        }}>
        {children}
      </Link>
    );
  } else {
    return (
      <TouchableWithoutFeedback {...rest} accessibilityRole={accessibilityRole} onPress={onPress}>
        <View style={style}>{children}</View>
      </TouchableWithoutFeedback>
    );
  }
};

export const BottomTabItem = ({
  focused,
  route,
  icon,
  badge,
  to,
  button = defaultButtonRender,
  onPress,
  onLongPress,
  horizontal,
  uscIsBlocked,
  style,
}: Props) => {
  const {colors} = useTheme();
  const activeTintColor = colors.primary;
  const inactiveTintColor = Color(colors.text).mix(Color(colors.card), 0.5).hex();

  const renderIcon = ({focused: isFocused}: {focused: boolean}) => {
    if (!icon) {
      return null;
    }

    const activeOpacity = isFocused ? 1 : 0;
    const inactiveOpacity = isFocused ? 0 : 1;

    return (
      <TabBarIcon
        route={route}
        horizontal={horizontal}
        badge={badge}
        activeOpacity={activeOpacity}
        inactiveOpacity={inactiveOpacity}
        activeTintColor={activeTintColor}
        inactiveTintColor={inactiveTintColor}
        renderIcon={icon}
        style={{}}
      />
    );
  };

  const scene = {route, focused};

  const backgroundColor = 'transparent';

  return button({
    to,
    onPress,
    onLongPress,
    accessibilityRole: 'button',
    accessibilityState: {selected: focused},
    // @ts-expect-error: keep for compatibility with older React Native versions
    accessibilityStates: focused ? ['selected'] : [],
    touchSoundDisabled: uscIsBlocked && route.name === 'qrCodeCard',
    style: [styles.tab, {backgroundColor}, horizontal ? styles.tabLandscape : styles.tabPortrait, style],
    children: <React.Fragment>{renderIcon(scene)}</React.Fragment>,
  }) as React.ReactElement;
};

const styles = StyleSheet.create({
  tab: {
    flex: 1,
    alignItems: 'center',
  },
  tabPortrait: {
    justifyContent: 'flex-end',
    flexDirection: 'column',
  },
  tabLandscape: {
    justifyContent: 'center',
    flexDirection: 'row',
  },
  label: {
    textAlign: 'center',
    backgroundColor: 'transparent',
  },
  labelBeneath: {
    fontSize: 10,
  },
  labelBeside: {
    fontSize: 13,
    marginLeft: 20,
    marginTop: 3,
  },
  button: {
    display: 'flex',
  },
});
